import { PureComponent } from 'react';
import { layer, source, feature } from 'react-openlayers';
import { orderBy } from 'lodash';

import { KML_FEATURE_TYPE } from '../../../featureTypes';
import { WHITE } from '../../../rgbColors';
import { COLOR, CORE_DEADBAND_WIDTH_M } from '../../../constants';
import { colorToString } from '../../../utils';
import { MAP_TYPES, MapType } from '../helpers/mapTypes';
import { getCorePointKey } from '../helpers/features';
import { DefaultKMLFeatureProperties, SelectCandidate, KMLFeature, JobType } from '../../../types/types';
import CorePoint from '../../../db/CorePointClass';
import { Sample, SoilCore } from '../../../db';
import EventBus from '../../../EventBus';
import { MAP_EVENTS } from '../../../mapEvents';
import { MISSION_EVENTS } from '../../../missionEvents';
import { SESSION_EVENTS } from '../../../sessionEvents';
import { getCurrentMission, getCurrentSession } from '../../../dataModelHelpers';
import { getMemoryTable } from '../../../db/datamanager';
import { MeasuredCircle } from '../features/measured_circle';
import {
  MapDebugCloseCoreTolerance,
  MapDebugCoreLabels,
  MapDebugCoreWaypointNumbers,
  MapDebugEdgeUnpulledCores,
  MapDebugNoSampleCoreTolerance,
  MapDebugPresentCoreTolerance,
  MapPixelsPerMeterStorage,
} from '../../../db/local_storage';
import { hideAtZoom, HIDE_AT_ZOOM_ZONE, HIDE_TEXT_ZOOM } from './zoomThresholds';

type AddSelectionCandidate = (candidate: SelectCandidate | null) => void;

interface CorePointsLayerProps {
  jobType: JobType;
  zoneMission: boolean;
  type: MapType;
  // Selection candidates — for step 1 of the split these are the same
  // callbacks KmlLayer uses, so we can plug in without changing
  // CommonMap's API surface.
  addDeleteCandidate: AddSelectionCandidate;
  addSkipCandidate: AddSelectionCandidate;
  addWaypointCandidate: AddSelectionCandidate;
  addMoveCandidate: AddSelectionCandidate;
  selectInteractionDisabled: boolean;
  modifyInteractionDisabled: boolean;
}

interface CorePointsLayerState {
  // Flat list of JSX elements in mission-sample order, one or more per
  // soil core. render() wraps this in a VectorSourceReact.
  features: JSX.Element[];
  presentSampleId: string;
  closeSampleId: string;
  activeSampleId: string | undefined;
}

const Z_INDEX_CORE = 3;
const Z_INDEX_FIRST_LAST = 3;

/**
 * CorePointsLayer — focused layer that renders ONLY the soil core point
 * features of the current mission. Split out of the monolithic KmlLayer
 * so that a core completion (operator pressing Shift+C) only touches a
 * small targeted JSX list, instead of rebuilding thousands of unrelated
 * features (zones, centroids, path, static points, boundaries).
 *
 * The layer owns its own EventBus subscriptions: MISSION_EVENTS.UPDATED /
 * CREATED_OR_DELETED, MAP_EVENTS.CORES_NEED_UPDATE (full rebuild),
 * MAP_EVENTS.SAMPLES_UPDATED (targeted rebuild), and SAMPLE_ARRIVED /
 * SAMPLE_LEFT / CLOSE_SAMPLE_ARRIVED / CLOSE_SAMPLE_LEFT (highlight
 * state only).
 */
export default class CorePointsLayer extends PureComponent<CorePointsLayerProps, CorePointsLayerState> {
  /**
   * Map from sample_id -> array of JSX features for that sample.
   * Kept alongside state.features so rebuildForSamples can replace just
   * the affected slice and preserve reference equality for other entries.
   */
  private featuresBySampleId: Map<string, JSX.Element[]> = new Map();

  /** Deterministic sample order used to flatten featuresBySampleId. */
  private sampleOrder: string[] = [];

  /** Microtask batch: multiple rebuildForSamples calls in the same
   *  tick merge into one rebuild. Avoids duplicate work when e.g.
   *  setBarcode + setSampleSelected both target sample '114'. */
  private pendingRebuildIds: Set<string> | null = null;

  /**
   * Cached (missionCorePoints, map-by-waypoint-number) pair. See comment
   * on KmlLayer.getMissionCorePoints — same invalidation strategy via
   * Waypoint + CorePoint table mutationCount. Captured AFTER the rebuild
   * so lazy _refs population doesn't cause cache misses.
   */
  private missionCorePointsCache?: {
    sorted: CorePoint[];
    byWaypointNumber: Map<number, CorePoint>;
    waypointMutations: number;
    corePointMutations: number;
  };

  constructor(props: CorePointsLayerProps) {
    super(props);

    this.state = {
      features: [],
      presentSampleId: '',
      closeSampleId: '',
      activeSampleId: undefined,
    };

    this.rebuildAll = this.rebuildAll.bind(this);
    this.onSamplesUpdated = this.onSamplesUpdated.bind(this);
    this.onCloseSampleNeedsUpdate = this.onCloseSampleNeedsUpdate.bind(this);
    this.onCurrentSampleChanged = this.onCurrentSampleChanged.bind(this);
    this.onPresentSampleChanged = this.onPresentSampleChanged.bind(this);
    this.onPresentSampleLeft = this.onPresentSampleLeft.bind(this);
    this.onCloseSampleChanged = this.onCloseSampleChanged.bind(this);
    this.onCloseSampleLeft = this.onCloseSampleLeft.bind(this);
  }

  componentDidMount() {
    // Intentionally NOT subscribing to MISSION_EVENTS.UPDATED here.
    // That event fires from ~77 call sites across the codebase, most
    // of which don't actually change cores — e.g. dragging the
    // parking point, toggling a map layer visibility checkbox,
    // renaming a zone. Subscribing would cause a full ~1000-core
    // rebuild on every such call.
    //
    // Cores are rebuilt via two narrower channels:
    //   - MAP_EVENTS.CORES_NEED_UPDATE (full rebuild) — fired by
    //     `dispatchMissionUpdated` (as a legacy catch-all) and by
    //     operations that definitely change cores.
    //   - MAP_EVENTS.SAMPLES_UPDATED (targeted rebuild of specific
    //     sample ids) — the fast path for Shift+C core completion,
    //     barcode scans, skip/unskip, etc.
    //
    // See Step 12 of docs/superpowers/plans/2026-04-08-kml-layer-split.md.
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.on(MAP_EVENTS.CORES_NEED_UPDATE, this.rebuildAll);
    // Initial build: if a mission is already loaded (e.g. switching
    // from maps tab to sampling tab), no event fires — render now.
    this.rebuildAll();
    EventBus.on(MAP_EVENTS.CLOSE_SAMPLE_NEEDS_UPDATE, this.onCloseSampleNeedsUpdate);
    EventBus.on(MAP_EVENTS.SAMPLES_UPDATED, this.onSamplesUpdated);
    EventBus.on(MAP_EVENTS.SAMPLE_ARRIVED, this.onPresentSampleChanged, true);
    EventBus.on(MAP_EVENTS.SAMPLE_LEFT, this.onPresentSampleLeft, true);
    EventBus.on(MAP_EVENTS.CLOSE_SAMPLE_ARRIVED, this.onCloseSampleChanged, true);
    EventBus.on(MAP_EVENTS.CLOSE_SAMPLE_LEFT, this.onCloseSampleLeft, true);
    EventBus.on(SESSION_EVENTS.UPDATED_CURRENT_SAMPLE, this.onCurrentSampleChanged);
  }

  componentDidUpdate(prevProps: CorePointsLayerProps, prevState: CorePointsLayerState) {
    // zoneMission affects label visibility — rebuild when it changes.
    // On Sampling tab mount, CommonMap.getZoneType() sets zoneMission
    // AFTER the initial rebuildAll (which runs with zoneMission=false).
    if (prevProps.zoneMission !== this.props.zoneMission) {
      this.rebuildAll();

      return;
    }

    // When the present/close sample changes, rebuild just those samples'
    // cores so their tolerance circles + highlighting are applied/removed.
    // Mirrors the behaviour that used to live in KmlLayer.componentDidUpdate.
    const presentChanged = this.state.presentSampleId !== prevState.presentSampleId;
    const closeChanged = this.state.closeSampleId !== prevState.closeSampleId;

    if (presentChanged) {
      const affected = [prevState.presentSampleId, this.state.presentSampleId].filter((id): id is string =>
        Boolean(id),
      );

      if (affected.length) {
        this.rebuildForSamples(affected);
      }
    }

    if (closeChanged) {
      const affected = [prevState.closeSampleId, this.state.closeSampleId].filter((id): id is string => Boolean(id));

      if (affected.length) {
        this.rebuildForSamples(affected);
      }
    }
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.CORES_NEED_UPDATE, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.CLOSE_SAMPLE_NEEDS_UPDATE, this.onCloseSampleNeedsUpdate);
    EventBus.remove(MAP_EVENTS.SAMPLES_UPDATED, this.onSamplesUpdated);
    EventBus.remove(MAP_EVENTS.SAMPLE_ARRIVED, this.onPresentSampleChanged);
    EventBus.remove(MAP_EVENTS.SAMPLE_LEFT, this.onPresentSampleLeft);
    EventBus.remove(MAP_EVENTS.CLOSE_SAMPLE_ARRIVED, this.onCloseSampleChanged);
    EventBus.remove(MAP_EVENTS.CLOSE_SAMPLE_LEFT, this.onCloseSampleLeft);
    EventBus.remove(SESSION_EVENTS.UPDATED_CURRENT_SAMPLE, this.onCurrentSampleChanged);
  }

  // ---------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------

  onSamplesUpdated(sampleIdsString?: string) {
    if (!sampleIdsString) {
      return;
    }

    let sampleIds: string[] = [];

    try {
      const parsed = JSON.parse(sampleIdsString) as { sampleIds: string[] };
      sampleIds = Array.isArray(parsed?.sampleIds) ? parsed.sampleIds : [];
    } catch {
      return;
    }

    if (sampleIds.length === 0) {
      return;
    }

    this.rebuildForSamples(sampleIds);
  }

  /**
   * Zoom/resolution changed — the tolerance circles drawn around the
   * close/present sample's cores are measured in pixels-per-meter, so
   * we need to rebuild just those samples' core JSX. All other samples
   * are unaffected. Replaces KmlLayer.onCloseSampleNeedsUpdate.
   */
  onCloseSampleNeedsUpdate() {
    if (this.props.type === MAP_TYPES.MAPS) {
      return;
    }

    const sampleIds: string[] = [];

    if (this.state.closeSampleId) {
      sampleIds.push(this.state.closeSampleId);
    }

    if (this.state.presentSampleId) {
      sampleIds.push(this.state.presentSampleId);
    }

    if (sampleIds.length === 0) {
      return;
    }

    this.rebuildForSamples(sampleIds);
  }

  onCurrentSampleChanged() {
    const session = getCurrentSession();
    const activeSampleId = session?.getSample()?.sample_id;

    if (activeSampleId !== this.state.activeSampleId) {
      const prev = this.state.activeSampleId;
      this.setState({ activeSampleId });
      // Only the old and new active samples' cores change colour —
      // rebuild just those two instead of the full layer.
      const affected = [prev, activeSampleId].filter((id): id is string => Boolean(id));

      if (affected.length) {
        this.rebuildForSamples(affected);
      }
    }
  }

  onPresentSampleChanged(sampleId: string) {
    if (this.state.presentSampleId === sampleId) {
      return;
    }

    this.setState({ presentSampleId: sampleId });
  }

  onPresentSampleLeft(sampleId: string) {
    if (this.state.presentSampleId !== sampleId) {
      return;
    }

    this.setState({ presentSampleId: '' });
  }

  onCloseSampleChanged(sampleId: string) {
    if (this.state.closeSampleId === sampleId) {
      return;
    }

    this.setState({ closeSampleId: sampleId });
  }

  onCloseSampleLeft(sampleId: string) {
    if (this.state.closeSampleId !== sampleId) {
      return;
    }

    this.setState({ closeSampleId: '' });
  }

  // ---------------------------------------------------------------------
  // Rebuild paths
  // ---------------------------------------------------------------------

  /**
   * Snapshot of the localStorage-backed debug flags for a single rebuild
   * pass. Reading these per-core in the hot loop is ~0.3ms per call in
   * JSDOM, which adds up to several ms on a big field. We read them once
   * per rebuild and thread them through the JSX generators.
   */
  private readRenderOptions() {
    return {
      pixelsPerMeter: MapPixelsPerMeterStorage.get(),
      mapDebugCoreLabels: MapDebugCoreLabels.get(),
      mapDebugCoreWaypointNumbers: MapDebugCoreWaypointNumbers.get(),
      mapDebugEdgeUnpulledCores: MapDebugEdgeUnpulledCores.get(),
      mapDebugCloseCoreTolerance: MapDebugCloseCoreTolerance.get(),
      mapDebugPresentCoreTolerance: MapDebugPresentCoreTolerance.get(),
      mapDebugNoSampleCoreTolerance: MapDebugNoSampleCoreTolerance.get(),
    };
  }

  rebuildAll() {
    console.count('CorePointsLayer.rebuildAll');
    const mission = getCurrentMission();

    if (!mission) {
      this.featuresBySampleId = new Map();
      this.sampleOrder = [];
      this.setState({ features: [] });

      return;
    }

    const opts = this.readRenderOptions();
    const { sorted: missionCorePoints, byWaypointNumber: corePointByWaypointNumber } = this.getMissionCorePoints();
    const activeSampleId = this.state.activeSampleId;

    const newMap = new Map<string, JSX.Element[]>();
    const order: string[] = [];

    for (const sample of mission.getSamples()) {
      const sampleJsx = this.buildFeaturesForSample(
        sample,
        missionCorePoints,
        corePointByWaypointNumber,
        activeSampleId,
        opts,
      );
      newMap.set(sample.sample_id, sampleJsx);
      order.push(sample.sample_id);
    }

    this.featuresBySampleId = newMap;
    this.sampleOrder = order;
    this.setState({ features: this.flattenFeatures() });
  }

  rebuildForSamples(sampleIds: string[]) {
    if (sampleIds.length === 0) {
      return;
    }

    // Batch: collect IDs and flush once per microtask. If two events
    // in the same tick both target sample '114' (e.g. setBarcode +
    // setSampleSelected), the rebuild runs only once.
    if (!this.pendingRebuildIds) {
      this.pendingRebuildIds = new Set(sampleIds);
      queueMicrotask(() => {
        const ids = Array.from(this.pendingRebuildIds!);
        this.pendingRebuildIds = null;
        this.doRebuildForSamples(ids);
      });
    } else {
      for (const id of sampleIds) {
        this.pendingRebuildIds.add(id);
      }
    }
  }

  private doRebuildForSamples(sampleIds: string[]) {
    console.log('CorePointsLayer.rebuildForSamples', sampleIds);
    const mission = getCurrentMission();

    if (!mission || sampleIds.length === 0) {
      return;
    }

    const sampleById = new Map<string, Sample>();

    for (const s of mission.getSamples()) {
      sampleById.set(s.sample_id, s);
    }

    const opts = this.readRenderOptions();
    const { sorted: missionCorePoints, byWaypointNumber: corePointByWaypointNumber } = this.getMissionCorePoints();
    const activeSampleId = this.state.activeSampleId;

    // Handle all three cases: create, update, delete (including rename).
    //   - Sample exists in mission: (re)build its JSX entry and add
    //     to sampleOrder if not already present.
    //   - Sample missing from mission but present in our map: it was
    //     deleted (or renamed to a different id) → remove stale
    //     entry so the flattened list reflects the new state.
    //   - Sample missing from both: no-op.
    let anyChange = false;

    for (const id of sampleIds) {
      const sample = sampleById.get(id);

      if (sample) {
        const sampleJsx = this.buildFeaturesForSample(
          sample,
          missionCorePoints,
          corePointByWaypointNumber,
          activeSampleId,
          opts,
        );
        this.featuresBySampleId.set(id, sampleJsx);

        if (!this.sampleOrder.includes(id)) {
          this.sampleOrder.push(id);
        }

        anyChange = true;
      } else if (this.featuresBySampleId.has(id)) {
        // Sample was deleted (or renamed to a different id).
        this.featuresBySampleId.delete(id);
        this.sampleOrder = this.sampleOrder.filter((x) => x !== id);
        anyChange = true;
      }
    }

    if (anyChange) {
      this.setState({ features: this.flattenFeatures() });
    }
  }

  // ---------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------

  private flattenFeatures(): JSX.Element[] {
    const out: JSX.Element[] = [];

    for (const sampleId of this.sampleOrder) {
      const sampleFeatures = this.featuresBySampleId.get(sampleId);

      if (sampleFeatures) {
        out.push(...sampleFeatures);
      }
    }

    return out;
  }

  /**
   * Returns the sorted missionCorePoints list + a waypoint_number -> CorePoint
   * map. Caches the pair and invalidates via Waypoint + CorePoint
   * table mutationCount. Capture table references once, read .mutationCount
   * from them after the rebuild so lazy _refs population (which bumps the
   * counters) doesn't cause cache misses on the next call.
   */
  private getMissionCorePoints(): {
    sorted: CorePoint[];
    byWaypointNumber: Map<number, CorePoint>;
  } {
    const waypointTable = getMemoryTable('Waypoint');
    const corePointTable = getMemoryTable('CorePoint');

    if (
      this.missionCorePointsCache &&
      this.missionCorePointsCache.waypointMutations === (waypointTable?.mutationCount ?? 0) &&
      this.missionCorePointsCache.corePointMutations === (corePointTable?.mutationCount ?? 0)
    ) {
      return this.missionCorePointsCache;
    }

    const mission = getCurrentMission()!;
    const sorted = orderBy(mission.getWaypoints().getCorePoints(), ['waypoint_number']);
    const byWaypointNumber = new Map<number, CorePoint>();

    for (const cp of sorted) {
      if (cp.waypoint_number !== undefined) {
        byWaypointNumber.set(cp.waypoint_number, cp);
      }
    }

    this.missionCorePointsCache = {
      sorted,
      byWaypointNumber,
      waypointMutations: waypointTable?.mutationCount ?? 0,
      corePointMutations: corePointTable?.mutationCount ?? 0,
    };

    return this.missionCorePointsCache;
  }

  /**
   * Build the JSX feature list for one sample's soil cores. Same logic
   * as KmlLayer.genCorePointFeatures, ported so this layer is
   * self-contained.
   */
  private buildFeaturesForSample(
    sample: Sample,
    missionCorePoints: CorePoint[],
    corePointByWaypointNumber: Map<number, CorePoint>,
    activeSampleId: string | undefined,
    opts: ReturnType<CorePointsLayer['readRenderOptions']>,
  ): JSX.Element[] {
    const out: JSX.Element[] = [];
    const soilCores = sample.getSoilCores();

    for (const soilCore of soilCores) {
      const kmlFeature = soilCore.to_feature(false);

      if (!kmlFeature) {
        continue;
      }

      // genKmlFeature used to set properties.index; buildFeaturesForSample
      // is the direct replacement so we set it here too for downstream
      // consumers that read properties.index. Cast through any because
      // KMLSoilCoreFeatureProperties doesn't declare index.
      (kmlFeature.properties as any).index = out.length;

      out.push(
        ...this.genCorePointFeatures(
          kmlFeature as any,
          missionCorePoints,
          corePointByWaypointNumber,
          activeSampleId,
          opts,
        ),
      );
    }

    return out;
  }

  // ---------------------------------------------------------------------
  // JSX generation — ported from KmlLayer.
  // These methods are intentionally kept faithful to the KmlLayer
  // originals so visual behaviour is unchanged. They read `this.props`
  // and `this.state` the same way KmlLayer did.
  // ---------------------------------------------------------------------

  private genCorePointFeatures(
    kmlFeature: KMLFeature<DefaultKMLFeatureProperties>,
    missionCorePoints: CorePoint[],
    corePointByWaypointNumber: Map<number, CorePoint>,
    activeSampleId: string | undefined,
    opts: ReturnType<CorePointsLayer['readRenderOptions']>,
  ): JSX.Element[] {
    if (!missionCorePoints.length) {
      return this.genCorePointFeature(undefined, missionCorePoints, kmlFeature, activeSampleId, opts);
    }

    const targetWaypointNumber = kmlFeature.properties.waypoint_index + 1;
    const corePoint = corePointByWaypointNumber.get(targetWaypointNumber);

    const out: JSX.Element[] = [];
    const isFirstCorePoint = corePoint === missionCorePoints[0];

    if (isFirstCorePoint) {
      const firstPointElement = this.genFirstLastPointFlag(kmlFeature, true);

      if (firstPointElement) {
        out.push(firstPointElement);
      }
    }

    const isLastCorePoint = corePoint === missionCorePoints[missionCorePoints.length - 1];

    if (isLastCorePoint) {
      const lastPointElement = this.genFirstLastPointFlag(kmlFeature, false);

      if (lastPointElement) {
        out.push(lastPointElement);
      }
    }

    return this.genCorePointFeature(corePoint, missionCorePoints, kmlFeature, activeSampleId, opts).concat(out);
  }

  private getFirstOrLastUnpulled(
    corePoint: CorePoint | undefined,
    kmlFeature: KMLFeature<DefaultKMLFeatureProperties, { coordinates?: any[] }>,
    allCorePoints: CorePoint[],
  ) {
    const isPulled = !!corePoint?.getSoilCore()?.pulled;
    const isUnpulled = !isPulled;

    let firstOrLastUnpulled = isUnpulled;

    if (!firstOrLastUnpulled) {
      return false;
    }

    // Only do the scan for the close sample — scanning for every core is
    // too expensive on large fields.
    if (this.state.closeSampleId !== kmlFeature.properties.sample_id) {
      return false;
    }

    const sampleCorePoints = allCorePoints.filter(
      (cp) => cp.getSoilCore()?.getSample()?.sample_id === kmlFeature.properties.sample_id,
    );

    const unpulledBefore = sampleCorePoints.find(
      (cp) =>
        cp.waypoint_number &&
        cp.waypoint_number < kmlFeature.properties.waypoint_index + 1 &&
        !cp.getSoilCore()?.pulled,
    );

    const unpulledAfter = sampleCorePoints.find(
      (cp) =>
        cp.waypoint_number &&
        cp.waypoint_number > kmlFeature.properties.waypoint_index + 1 &&
        !cp.getSoilCore()?.pulled,
    );

    firstOrLastUnpulled = !unpulledBefore || !unpulledAfter;

    return firstOrLastUnpulled;
  }

  private genCoreMapDebugFeatures(
    missionCorePoint: CorePoint | undefined,
    allCorePoints: CorePoint[],
    kmlFeature: KMLFeature<DefaultKMLFeatureProperties>,
    opts: ReturnType<CorePointsLayer['readRenderOptions']>,
  ): JSX.Element[] {
    let firstOrLastUnpulled = this.getFirstOrLastUnpulled(missionCorePoint, kmlFeature, allCorePoints);
    const mission = getCurrentMission()!;
    const presentTolerance = mission.get_present_tolerance();
    const closeTolerance = mission.get_close_tolerance();
    const pulled = kmlFeature.properties.pulled;

    if (!opts.mapDebugEdgeUnpulledCores) {
      firstOrLastUnpulled = !pulled;
    }

    const coords = kmlFeature.geometry.coordinates;

    if (!coords) {
      return [];
    }

    const showCoreToleranceCircle = (firstOrLastUnpulled: boolean) => {
      if (!opts.mapDebugPresentCoreTolerance) {
        return false;
      }

      if (mission.is_zone_mission()) {
        return true;
      }

      if (!firstOrLastUnpulled) {
        return false;
      }

      const sample = mission.getSamples().find((s: Sample) => s.sample_id === kmlFeature.properties.sample_id);
      const sampleHasPulledCores = sample ? sample.hasCoresPulled() : false;
      const edgeCoreWaypointNumber = kmlFeature.properties.waypoint_number;

      if (!sampleHasPulledCores) {
        return true;
      }

      const foundAdjasentCorePulled = sample!.getSoilCores().some((core: SoilCore) => {
        return (
          core.pulled &&
          (core.waypoint_number === edgeCoreWaypointNumber + 1 || core.waypoint_number === edgeCoreWaypointNumber - 1)
        );
      });

      return foundAdjasentCorePulled;
    };

    return [
      opts.mapDebugCloseCoreTolerance && !pulled && (
        <MeasuredCircle
          key={`${kmlFeature.properties.name}-close-core-tolerance`}
          center={coords}
          color="rgba(255, 128, 0, 0.2)"
          circleMeasurements
          innerRadiusMeters={presentTolerance}
          outerRadiusMeters={closeTolerance}
          pixelsPerMeter={opts.pixelsPerMeter}
        />
      ),
      showCoreToleranceCircle(firstOrLastUnpulled) && (
        <MeasuredCircle
          key={`${kmlFeature.properties.name}-core-tolerance`}
          center={coords}
          color={pulled ? '#ffa500' : 'rgba(255, 255, 0, 1)'}
          innerRadiusMeters={presentTolerance}
          outerRadiusMeters={presentTolerance}
          pixelsPerMeter={opts.pixelsPerMeter}
        />
      ),
      opts.mapDebugNoSampleCoreTolerance && !pulled && (
        <MeasuredCircle
          key={`${kmlFeature.properties.name}-no-sample-core-tolerance`}
          center={coords}
          color="rgba(255, 0, 0, 0.2)"
          circleMeasurements
          innerRadiusMeters={presentTolerance}
          outerRadiusMeters={presentTolerance + CORE_DEADBAND_WIDTH_M}
          pixelsPerMeter={opts.pixelsPerMeter}
        />
      ),
    ].filter((sel) => !!sel) as JSX.Element[];
  }

  private genCorePointFeature(
    missionCorePoint: CorePoint | undefined,
    allCorePoints: CorePoint[],
    kmlFeature: KMLFeature<DefaultKMLFeatureProperties>,
    activeSampleId: string | undefined,
    opts: ReturnType<CorePointsLayer['readRenderOptions']>,
  ): JSX.Element[] {
    const key = getCorePointKey(kmlFeature.properties);
    const sampled = this.props.zoneMission && Boolean(kmlFeature.properties.bag_id);
    const selectedSample =
      this.props.zoneMission &&
      kmlFeature.properties.sample_id === activeSampleId &&
      this.props.type === MAP_TYPES.SAMPLING;

    const uniqueKey = `${key}-${KML_FEATURE_TYPE.CORE_POINT}`;
    const pulled = kmlFeature.properties.pulled;

    const isZoneMission = this.props.zoneMission;
    const showCoreLabels = isZoneMission || opts.mapDebugCoreLabels;

    let fillColor: string;
    const skipped: boolean = kmlFeature.properties.skipped;

    if (skipped) {
      fillColor = colorToString(COLOR.RED);
    } else if (selectedSample) {
      fillColor = colorToString(COLOR.BLUE);
    } else if (sampled) {
      fillColor = colorToString(COLOR.GREEN);
    } else if (pulled) {
      fillColor = colorToString(COLOR.ORANGE);
    } else {
      fillColor = colorToString(COLOR.WHITE);
    }

    if (!kmlFeature.geometry.coordinates) {
      return [];
    }

    const showWaypointNumbers = opts.mapDebugCoreWaypointNumbers;

    let out: JSX.Element[] = [
      <feature.PointReact
        coordinate={kmlFeature.geometry.coordinates}
        circleOptions={{
          radius: isZoneMission ? 5 : 3,
          fillOptions: { color: fillColor },
          strokeOptions: { color: 'black', width: 1 },
          blinking: selectedSample,
        }}
        key={uniqueKey}
        zIndex={Z_INDEX_CORE}
        textOptions={
          showCoreLabels
            ? {
                text: `${kmlFeature.properties.name}${showWaypointNumbers ? ' wp ' + kmlFeature.properties.waypoint_number : ''}`,
                font: '18px Calibri,sans-serif',
                fillOptions: { color: WHITE },
                offsetX: sampled ? 25 : 20,
              }
            : {}
        }
        hideAtZoom={hideAtZoom(this.props.zoneMission)}
        hideTextZoom={HIDE_TEXT_ZOOM}
        properties={kmlFeature.properties}
      />,
    ];

    const isCloseSample = kmlFeature.properties.sample_id === this.state.closeSampleId;
    const isPresentSample = kmlFeature.properties.sample_id === this.state.presentSampleId;

    if (this.props.type === MAP_TYPES.SAMPLING && (isCloseSample || isPresentSample)) {
      out = out.concat(...this.genCoreMapDebugFeatures(missionCorePoint, allCorePoints, kmlFeature, opts));
    }

    return out;
  }

  private genFirstLastPointFlag(kmlFeature: KMLFeature<DefaultKMLFeatureProperties>, first: boolean) {
    const uniqueKey = `${first ? 'first' : 'last'}`;

    if (!kmlFeature.geometry.coordinates) {
      return null;
    }

    return (
      <feature.PointReact
        // @ts-ignore - Coordinate type from ol
        coordinate={kmlFeature.geometry.coordinates}
        // @ts-ignore - coordinates is unknown[]
        key={`${uniqueKey}-${kmlFeature.geometry.coordinates[0]}`}
        zIndex={Z_INDEX_FIRST_LAST}
        iconOptions={
          first
            ? {
                src: './images/start-flag.png',
                anchor: [0.1, 1.2],
                scale: 0.2,
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                opacity: 1,
              }
            : {
                src: './images/finish-flag.png',
                anchor: [0.1, 1.2],
                scale: 0.2,
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                opacity: 1,
              }
        }
        hideAtZoom={HIDE_AT_ZOOM_ZONE}
      />
    );
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  render() {
    return (
      // @ts-ignore - layer.Vector children typing
      <layer.VectorImage zIndex={Z_INDEX_CORE}>
        {/* @ts-ignore - source.VectorSourceReact children typing */}
        <source.VectorSourceReact>{this.state.features}</source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
